/* @flow */
import cx from 'classnames';
import * as React from 'react';
import invariant from 'invariant';
import { keyBy } from 'lodash';

import { getHeader } from './generators/Header';
import type { IconName } from 'components/Icon';
import Table from 'components/Table';
import TableRow from 'components/TableRow';
import Text from 'components/Text';

import getField from './ComposableTableField';
import type { Field } from './ComposableTableField';

import './styles/ComposableTable.scss';

type ColumnHeaderIcon = {
  iconName: IconName,
  tooltip: string,
};

export type ColumnHeader = {
  icon?: ColumnHeaderIcon,
  name: string,
  sortValue?: string,
  type?: 'avatar',
  // `width` of cell content not including sort icons, tooltipped icons, or cell padding
  width?: number,
};

export type Props = {
  className?: string,
  columnHeaders?: ColumnHeader[],
  columnGenerators: Function[],
  rows: Object[],
  keysOfActiveRows?: any[],
  keyField: Field,
  actionsColumn?: boolean,
  handleSort?: Function,
  handleRowClick?: Function,
  loading?: boolean,
  fixedHeader?: boolean,
  topOffset?: number,
  // `spreadsheet` adds column letters and row numbers
  // so that the table looks like a spreadsheet
  spreadsheet?: boolean,
};

export type SortDirection = 'asc' | 'desc';

type State = {
  sortColumn: ?string,
  sortDirection: ?SortDirection,
};

class ComposableTable extends React.Component<Props, State> {
  state: State = {
    sortColumn: null,
    sortDirection: null,
  };

  generateHeaders = (columnHeaders: ColumnHeader[]): Array<React.Node> => {
    const { sortColumn, sortDirection } = this.state;
    const { topOffset, spreadsheet } = this.props;

    const generatedHeaders = columnHeaders.map((header, index) => {
      const sortProps = header.sortValue
        ? {
            onClick: this.handleSort,
            sortColumn,
            sortDirection,
          }
        : null;

      return getHeader(header, sortProps, topOffset, index, spreadsheet);
    });

    if (this.props.spreadsheet) {
      // add an empty column header to accommodate the row numbers
      const emptyColumnHeader = (
        <th key="line-numbers" className="ComposableTableHeader" style={{ width: 20 }} />
      );
      return [emptyColumnHeader, ...generatedHeaders];
    }

    return generatedHeaders;
  };

  handleSort = (sortValue: string, sortDirection: SortDirection) => {
    this.setState({ sortColumn: sortValue, sortDirection });

    if (this.props.handleSort) {
      this.props.handleSort(sortValue, sortDirection);
    }
  };

  buildRow = (row: *, index: number): * => {
    const generatedRow = this.props.columnGenerators.map((generator, i) => {
      const key = i;
      return <td key={key}>{generator(row)}</td>;
    });

    if (this.props.spreadsheet) {
      // add a cell at the beginning of the row with the
      // row number
      const rowNumberCell = (
        <td key="line-number">
          <Text color="wolf">{index}</Text>
        </td>
      );
      return [rowNumberCell, ...generatedRow];
    }
    return generatedRow;
  };

  renderTableBody = (keyField: Field, rows: *[]): * => {
    const { handleRowClick, keysOfActiveRows } = this.props;

    const activeRowsKeyHash = keyBy(keysOfActiveRows, key => key);

    return rows.map((row, index) => {
      const key = getField(row, keyField);
      return (
        <TableRow
          hoverable={Boolean(handleRowClick) || Boolean(row.onClick)}
          onClick={() => {
            if (handleRowClick) {
              handleRowClick(row);
            }
            if (row.onClick) {
              row.onClick();
            }
          }}
          key={key}
          active={key in activeRowsKeyHash}>
          {this.buildRow(row, index)}
        </TableRow>
      );
    });
  };

  render() {
    const {
      actionsColumn,
      className,
      columnGenerators,
      columnHeaders,
      keyField,
      handleSort,
      rows,
      fixedHeader,
      loading,
    } = this.props;

    if (columnHeaders) {
      invariant(
        columnGenerators.length === columnHeaders.length,
        "Won't have the same number of headers as columns!"
      );
      invariant(
        columnHeaders.some(header => header.sortValue) ? handleSort : true,
        'Missing sortable function for sorting columns'
      );
    }

    return (
      <Table
        actionsColumn={actionsColumn}
        fixedHeader={fixedHeader}
        head={columnHeaders ? <tr>{this.generateHeaders(columnHeaders)}</tr> : null}
        loading={loading || !rows}
        className={cx('ComposableTable', className)}>
        <tbody>{rows && this.renderTableBody(keyField, rows)}</tbody>
      </Table>
    );
  }
}

export default ComposableTable;
